import React, {useEffect, useRef, useState} from 'react';
import NomenclatureTemplate from './NomenclatureTemplate';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ApiRequest from '../StdFunction';
import CallNotification from '../../context/NotificationContext';
import {ConfirmDialog} from '../../ui/ConfirmDialog';
import {IReplicationNomenclature} from '../../interfaces/fetch/Nomenclature';

const ntinDetailsStatusLabels: Record<string, string> = {
    not_found: 'Не найден в НКТ',
    deactivated: 'Деактивирован в НКТ',
    error: 'Ошибка обработки',
    skipped: 'Некорректный штрихкод',
};

const ntinDetailsStatusLabel = (item: any): string => {
    if (item.status === 'skipped' && item.note) {
        return item.note;
    }
    return ntinDetailsStatusLabels[item.status] ?? item.status;
};

const ntinDetailsStatusColor: Record<string, 'warning' | 'error' | 'default'> = {
    not_found: 'warning',
    deactivated: 'warning',
    error: 'error',
    skipped: 'default',
};

const formatNtinDate = (value?: string | null): string => {
    if (!value) {
        return '';
    }
    const [date, time] = value.split(' ');
    if (!date || !time) {
        return value;
    }
    const [year, month, day] = date.split('-');
    return `${day}.${month}.${year} ${time.slice(0, 5)}`;
};

export default function NomenclatureNtinPage({setCurrentPageClass, userId, isAdmin}: IReplicationNomenclature) {
    const {handleErrorNotification, handleSuccessNotification} = CallNotification();

    const [refreshStatus, setRefreshStatus] = useState<any | null>(null);
    const [detailsItems, setDetailsItems] = useState<any[]>([]);
    const [detailsDuplicates, setDetailsDuplicates] = useState<any[]>([]);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [isStarting, setIsStarting] = useState(false);
    const [statusLoaded, setStatusLoaded] = useState(false);
    const refreshWasActive = useRef(false);
    const loadedTaskId = useRef<number | null | undefined>(undefined);

    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.down('sm'));

    const isActive = refreshStatus?.status === 'pending' || refreshStatus?.status === 'processing';
    const isThrottled = refreshStatus?.refresh_available === 0 && !isActive;
    const isFinished = refreshStatus?.status === 'done' || refreshStatus?.status === 'error';

    const fetchRefreshStatus = async () => {
        if (!userId) {
            return;
        }
        const response = await ApiRequest({
            method: 'GetNtinRefreshStatus',
            params: {user_id: Number(userId)},
        });
        if (response && !response.data.error) {
            setRefreshStatus(response.data.ntin_refresh ?? null);
        }
        setStatusLoaded(true);
    };

    const fetchDetails = async (taskId: number | null) => {
        if (!userId) {
            return;
        }
        setDetailsLoading(true);
        const response = await ApiRequest(
            {
                method: 'GetNtinRefreshDetails',
                params: {
                    user_id: Number(userId),
                    ...(taskId ? {task_id: taskId} : {}),
                },
            },
            handleErrorNotification,
        );
        if (response && !response.data.error) {
            setDetailsItems(response.data.ntin_refresh?.items ?? []);
            setDetailsDuplicates(response.data.ntin_refresh?.duplicates ?? []);
        }
        setDetailsLoading(false);
    };

    const handleStartRefresh = async () => {
        if (!userId) {
            return;
        }
        setConfirmOpen(false);
        setIsStarting(true);
        const response = await ApiRequest(
            {
                method: 'RefreshNtin',
                params: {user_id: Number(userId)},
            },
            handleErrorNotification,
        );
        if (!response?.data.error) {
            handleSuccessNotification({
                data: {message: 'Заявка на обновление НТИН запущена и будет обработана в течение суток'},
            });
            setRefreshStatus(response?.data.ntin_refresh ?? {status: 'pending'});
        }
        setIsStarting(false);
    };

    useEffect(() => {
        if (userId) {
            void fetchRefreshStatus();
        }
    }, [userId]);

    useEffect(() => {
        if (!isActive) {
            return;
        }
        const timer = setTimeout(() => {
            void fetchRefreshStatus();
        }, 15000);
        return () => clearTimeout(timer);
    }, [refreshStatus]);

    useEffect(() => {
        if (isActive) {
            refreshWasActive.current = true;
            return;
        }
        if (!refreshWasActive.current || !refreshStatus) {
            return;
        }
        refreshWasActive.current = false;
        if (refreshStatus.status === 'done') {
            const parts = [
                `обновлено: ${refreshStatus.updated_count ?? 0}`,
                `без изменений: ${refreshStatus.unchanged_count ?? 0}`,
                `не найдено в НКТ: ${refreshStatus.not_found_count ?? 0}`,
            ];
            if (refreshStatus.deactivated_count) {
                parts.push(`деактивировано: ${refreshStatus.deactivated_count}`);
            }
            if (refreshStatus.error_count) {
                parts.push(`ошибок: ${refreshStatus.error_count}`);
            }
            if (refreshStatus.skipped_count) {
                parts.push(`пропущено (некорректный ШК): ${refreshStatus.skipped_count}`);
            }
            if (refreshStatus.collapsed_count) {
                parts.push(`дублей ШК объединено: ${refreshStatus.collapsed_count}`);
            }
            handleSuccessNotification({data: {message: `Обновление НТИН завершено — ${parts.join(', ')}`}});
        } else if (refreshStatus.status === 'error') {
            handleErrorNotification({data: {error: 1, error_description: 'Обновление НТИН завершилось с ошибкой'}});
        }
    }, [refreshStatus]);

    useEffect(() => {
        if (!isFinished || !userId) {
            return;
        }
        const taskId = refreshStatus?.task_id ?? null;
        if (loadedTaskId.current === taskId) {
            return;
        }
        loadedTaskId.current = taskId;
        void fetchDetails(taskId);
    }, [refreshStatus, userId]);

    const progressValue = refreshStatus?.total ? Math.min(100, ((refreshStatus?.processed ?? 0) / refreshStatus.total) * 100) : 0;

    return (
        <NomenclatureTemplate setCurrentPageClass={setCurrentPageClass} isAdmin={isAdmin} userId={userId}>
            <Box sx={{maxWidth: 1000, mx: 'auto', p: 3}}>
                <Typography variant={isSm ? 'h5' : 'h4'} gutterBottom sx={{mb: isSm ? 2 : 3}}>
                    Обновление НТИН
                </Typography>
                <Typography variant="body2" sx={{mb: isSm ? 2 : 3}}>
                    НТИН всех позиций номенклатуры заново запрашивается в Национальном каталоге товаров (НКТ) и перезаписывается. Дубликаты
                    штрихкодов, отличающиеся только ведущим нулём, объединяются автоматически.
                </Typography>

                <Paper elevation={2} sx={{p: 3, mb: 3}}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Запуск обновления
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                        Заявка выполняется в фоне и будет обработана в течение суток. Запускать обновление можно не чаще одного раза в сутки.
                        Прогресс и итог отображаются на этой странице.
                    </Typography>
                    {isActive ? (
                        <Box>
                            <Typography variant="body2" sx={{mb: 1}}>
                                Выполняется обновление: {refreshStatus?.processed ?? 0} из {refreshStatus?.total ?? 0}
                            </Typography>
                            <LinearProgress
                                variant={refreshStatus?.total ? 'determinate' : 'indeterminate'}
                                value={progressValue}
                                sx={{height: 8, borderRadius: 4}}
                            />
                        </Box>
                    ) : (
                        <Box>
                            <Button
                                variant="contained"
                                startIcon={isStarting ? <CircularProgress size={20} color="inherit" /> : <AutorenewIcon />}
                                disabled={!userId || !statusLoaded || isStarting || isThrottled}
                                onClick={() => setConfirmOpen(true)}
                            >
                                Обновить НТИН
                            </Button>
                            {isThrottled && (
                                <Typography variant="body2" color="text.secondary" sx={{mt: 1.5}}>
                                    {refreshStatus?.next_refresh
                                        ? `Следующий запуск будет доступен ${formatNtinDate(refreshStatus.next_refresh)}`
                                        : 'Обновление уже запускалось менее суток назад'}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Paper>

                {isFinished && (
                    <Paper elevation={2} sx={{p: 3, mb: 3}}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{mb: 1.5}}>
                            Последний результат
                        </Typography>
                        {(refreshStatus?.finished || refreshStatus?.created) && (
                            <Typography sx={{mb: 1.5}} variant="body2" color="text.secondary">
                                {refreshStatus?.finished
                                    ? `Завершено: ${formatNtinDate(refreshStatus.finished)}`
                                    : `Заявка создана: ${formatNtinDate(refreshStatus.created)}`}
                            </Typography>
                        )}
                        {refreshStatus?.status === 'error' && (
                            <Typography variant="body2" color="error" sx={{mb: 1.5}}>
                                Обновление завершилось с ошибкой. Попробуйте запустить его позже или обратитесь в службу поддержки.
                            </Typography>
                        )}
                        {refreshStatus?.status === 'done' && (
                            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5}}>
                                <Chip color="success" variant="outlined" label={`Обновлено: ${refreshStatus?.updated_count ?? 0}`} />
                                <Chip variant="outlined" label={`Без изменений: ${refreshStatus?.unchanged_count ?? 0}`} />
                                {!!refreshStatus?.not_found_count && (
                                    <Chip color="warning" variant="outlined" label={`Не найдено в НКТ: ${refreshStatus.not_found_count}`} />
                                )}
                                {!!refreshStatus?.deactivated_count && (
                                    <Chip color="warning" variant="outlined" label={`Деактивировано: ${refreshStatus.deactivated_count}`} />
                                )}
                                {!!refreshStatus?.error_count && (
                                    <Chip color="error" variant="outlined" label={`Ошибок: ${refreshStatus.error_count}`} />
                                )}
                                {!!refreshStatus?.skipped_count && (
                                    <Chip variant="outlined" label={`Некорректный ШК: ${refreshStatus.skipped_count}`} />
                                )}
                                {!!refreshStatus?.collapsed_count && (
                                    <Chip variant="outlined" label={`Дублей объединено: ${refreshStatus.collapsed_count}`} />
                                )}
                            </Box>
                        )}
                        {detailsLoading ? (
                            <Box display="flex" justifyContent="center" py={3}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                {detailsItems.length ? (
                                    <>
                                        <Typography variant="subtitle1" fontWeight={600} sx={{mb: 1.5}}>
                                            Позиции, требующие внимания
                                        </Typography>
                                        <Typography variant="body2" sx={{mb: 1.5}} color="text.secondary">
                                            НТИН этих позиций обновить не удалось — проверьте штрихкод или удалите позицию.
                                        </Typography>
                                        <TableContainer sx={{maxHeight: '50vh', border: '1px solid #e0e4f0', borderRadius: '8px'}}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{backgroundColor: '#f5f7ff', fontWeight: 600}}>Штрихкод</TableCell>
                                                        <TableCell sx={{backgroundColor: '#f5f7ff', fontWeight: 600}}>Наименование</TableCell>
                                                        <TableCell sx={{backgroundColor: '#f5f7ff', fontWeight: 600}}>Статус</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {detailsItems.map((item, index) => (
                                                        <TableRow key={index} sx={{'&:last-child td': {borderBottom: 0}}}>
                                                            <TableCell sx={{whiteSpace: 'nowrap'}}>{item.barcode || '—'}</TableCell>
                                                            <TableCell>{item.name ?? '—'}</TableCell>
                                                            <TableCell sx={{whiteSpace: 'nowrap'}}>
                                                                <Chip
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color={ntinDetailsStatusColor[item.status] ?? 'default'}
                                                                    label={ntinDetailsStatusLabel(item)}
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                ) : (
                                    refreshStatus?.status === 'done' && <Typography>Все позиции обработаны, проблемных нет</Typography>
                                )}
                                {detailsDuplicates.length > 0 && (
                                    <>
                                        <Typography variant="subtitle1" fontWeight={600} sx={{mt: 3, mb: 1.5}}>
                                            Объединённые дубликаты штрихкодов
                                        </Typography>
                                        <Typography variant="body2" sx={{mb: 1.5}} color="text.secondary">
                                            Позиции, штрихкод которых отличался от другой позиции только ведущим нулём, объединены: осталась
                                            позиция с коротким штрихкодом, дубль удалён из номенклатуры. Если НТИН пары различался, в позиции
                                            оставлен НТИН актуальной карточки НКТ — такие строки отмечены, проверьте их.
                                        </Typography>
                                        <TableContainer sx={{maxHeight: '40vh', border: '1px solid #e0e4f0', borderRadius: '8px'}}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{backgroundColor: '#f5f7ff', fontWeight: 600}}>Штрихкод</TableCell>
                                                        <TableCell sx={{backgroundColor: '#f5f7ff', fontWeight: 600}}>
                                                            Удалённый дубль
                                                        </TableCell>
                                                        <TableCell sx={{backgroundColor: '#f5f7ff', fontWeight: 600}}>Наименование</TableCell>
                                                        <TableCell sx={{backgroundColor: '#f5f7ff', fontWeight: 600}}>НТИН</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {detailsDuplicates.map((pair, index) => (
                                                        <TableRow key={index} sx={{'&:last-child td': {borderBottom: 0}}}>
                                                            <TableCell sx={{whiteSpace: 'nowrap'}}>{pair.barcode_short}</TableCell>
                                                            <TableCell sx={{whiteSpace: 'nowrap'}}>{pair.barcode_long}</TableCell>
                                                            <TableCell>{pair.name ?? '—'}</TableCell>
                                                            <TableCell sx={{whiteSpace: 'nowrap'}}>
                                                                <Chip
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color={pair.differed ? 'warning' : 'success'}
                                                                    label={
                                                                        pair.differed
                                                                            ? `НТИН различался, оставлен ${pair.ntin || '—'}`
                                                                            : 'НТИН совпадал'
                                                                    }
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                )}
                            </>
                        )}
                    </Paper>
                )}
            </Box>
            <ConfirmDialog
                open={confirmOpen}
                title="Обновление НТИН"
                content={
                    <>
                        <Typography sx={{mb: 1.5}}>
                            НТИН всех товаров будет заново запрошен в НКТ и перезаписан. Запустить обновление можно не чаще одного раза в сутки.
                        </Typography>
                        <Typography sx={{mb: 1.5}}>
                            Заявка выполняется в фоне и будет обработана в течение суток. Прогресс и итог будут отображаться на этой странице.
                        </Typography>
                        <Typography>Продолжить?</Typography>
                    </>
                }
                confirmText="Запустить"
                isLoading={isStarting}
                onConfirm={() => void handleStartRefresh()}
                onClose={() => setConfirmOpen(false)}
            />
        </NomenclatureTemplate>
    );
}
